import { ColumnDef, createColumnHelper, SortingFn } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { createExpanderCell } from 'components/table/DataTableUtil';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';
import { MeasureFormat } from 'components/measure-unit/MeasureFormat';
import { MeasureComparison } from 'components/MeasureComparison';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { useModelService, Measure } from 'services/model/model.service';
import { useArticleVisibilityMeasures } from 'pages/ArticleVisibility/articleVisibilityMeasures';
import { Service } from 'services/data.service';
import { EmptyState } from 'components/EmptyState';
import { useMemo } from 'react';
import {
  ArticleVisibilityRow,
  ArticleVisibilityMeasureCell,
  TABLE_MEASURE_KEYS,
  NONE_ONLINE_FLAG,
  type ArticleVisibilityMeasureKey,
} from '../../model';
import {
  ArticleVisibilityInfo,
  ArticleVisibilityInfoButton,
} from 'pages/ArticleVisibility/components/ArticleVisibilityInfo/ArticleVisibilityInfo';

interface ArticleVisibilityBreakdownTableProps {
  rows: ArticleVisibilityRow[];
  isError?: boolean;
  errorMessage?: string | null;
}

function isRowAllZeros(row: ArticleVisibilityRow): boolean {
  return TABLE_MEASURE_KEYS.every(key => row[key].value === 0);
}

function getMeasureCell(row: ArticleVisibilityRow, key: ArticleVisibilityMeasureKey): ArticleVisibilityMeasureCell {
  return row[key];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sortMeasureCell: SortingFn<any> = (rowA, rowB, columnId) => {
  const a: ArticleVisibilityMeasureCell = rowA.getValue(columnId);
  const b: ArticleVisibilityMeasureCell = rowB.getValue(columnId);
  return a.value === b.value ? 0 : a.value > b.value ? 1 : -1;
};

function MeasureCellRenderer({
  cell,
  unit,
  interpretation,
}: {
  cell: ArticleVisibilityMeasureCell;
  unit: MeasureUnitCode;
  interpretation: number;
}) {
  if (cell.flags?.includes(NONE_ONLINE_FLAG)) {
    return (
      <div className="w-full flex items-center">
        <span className="text-sm text-gray-400">
          <FormattedMessage id="articleVisibility.tables.cell.notAvailable" defaultMessage="not available" />
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col xl:flex-row xl:items-center justify-between">
      <MeasureFormat value={cell.value} unit={unit} />
      {cell.rate !== undefined && (
        <MeasureComparison
          size="medium"
          value={cell.value}
          comparisonValue={cell.rate}
          unit={MeasureUnitCode.RATE}
          interpretation={interpretation}
        />
      )}
    </div>
  );
}

const columnHelper = createColumnHelper<ArticleVisibilityRow>();

function buildColumns(measures: Measure[], intl: ReturnType<typeof useIntl>): ColumnDef<ArticleVisibilityRow>[] {
  const measureColumns = measures.map((measure, index) => {
    const key = TABLE_MEASURE_KEYS[index];
    const unit = measure.metric.unit;
    const interpretation = measure.metric.interpretation ?? 1;
    return columnHelper.accessor(row => getMeasureCell(row, key), {
      id: key,
      header: () => measure.metric.name,
      cell: context => <MeasureCellRenderer cell={context.getValue()} unit={unit} interpretation={interpretation} />,
      sortingFn: sortMeasureCell,
    });
  });

  return [
    columnHelper.accessor('name', {
      header: () =>
        intl.formatMessage({
          id: 'articleVisibility.tables.marketplace.column',
          defaultMessage: 'Marketplaces',
        }),
      cell: context => createExpanderCell(context, <>{context.getValue()}</>, true),
    }),
    ...measureColumns,
  ] as ColumnDef<ArticleVisibilityRow>[];
}

export function ArticleVisibilityBreakdownTable({
  rows,
  isError = false,
  errorMessage = null,
}: ArticleVisibilityBreakdownTableProps) {
  const intl = useIntl();
  const articleVisibilityMeasureProvider = useArticleVisibilityMeasures();
  const { getMeasures } = useModelService(articleVisibilityMeasureProvider);
  const measures = getMeasures(TABLE_MEASURE_KEYS);

  const columns = useMemo(() => buildColumns(measures, intl), [measures, intl]);

  const exportConfigOptions: DataTableExportConfigOptions = {
    reportDownloadProps: {
      service: Service.ArticleVisibility,
      measuresGroup: 'export',
      tableName: 'ArticleVisibility',
      isArticleVisibility: true,
      isProReport: true,
      disabled: isError,
    },
  };

  if (!isError && rows.every(isRowAllZeros)) {
    return <EmptyState />;
  }

  return (
    <DataTableContextProvider>
      <DataTable
        data-testid="av-breakdown-table"
        titleId="articleVisibility.breakdown_table"
        additionalTitle={
          <ArticleVisibilityInfoButton titleKey="articleVisibility.tables.drawer.title">
            <ArticleVisibilityInfo
              titleTranslation="articleVisibility.tables"
              subTitleTranslation="articleVisibility.tables"
            />
          </ArticleVisibilityInfoButton>
        }
        rows={rows}
        columns={columns}
        defaultSortingState={[{ id: 'name', desc: true }]}
        exportConfigOptions={exportConfigOptions}
        isError={isError}
        errorMessage={errorMessage}
        subtitleBySortedColumn={false}
      />
    </DataTableContextProvider>
  );
}
